import type { FieldPath } from 'react-hook-form';

import {
  type GetPortalMetadataQuery,
  type GetPortalQuery,
  Portals_Types_Enum_Enum,
} from '@graphql/__generated__/graphql';
import type {
  BaseFieldDefinition,
  FormDefinitionType,
} from '@modules/_shared/components/form/RaForm';

type PortalSettingsContext = {
  shared_portals: GetPortalMetadataQuery['shared_portals'];
  portals_types_enum: GetPortalMetadataQuery['portals_types_enum'];
  disableTeamId: boolean;
  isCreation: boolean;
  readOnly: boolean;
  registerImage: (url: string) => Promise<{ id: string } | null>;
};

export type PortalSettingsFormData = Omit<
  NonNullable<GetPortalQuery['portals_by_pk']>,
  '__typename' | 'id' | 'updated_at' | 'portal_listings_aggregate' | 'logo'
> & { logo_image_id: string | null };

const defaultFormElement: Pick<
  BaseFieldDefinition<PortalSettingsFormData>,
  'render' | 'required' | 'gridProps'
> = {
  render: ({ type }) => !!type && type !== Portals_Types_Enum_Enum.Shared,
  required: true,
  gridProps: {
    xs: 12,
    md: 6,
  },
};

export const formDefinition: FormDefinitionType<
  PortalSettingsFormData,
  PortalSettingsContext
> = ({ t, context }) => [
  {
    type: 'image',
    name: 'logo_image_id',
    label: t('Portal logo'),
    render: () => !context.isCreation,
    disabled: () => context.readOnly,
    onFileUpload: async file => {
      const image = await context.registerImage(file.url);
      return image?.id ?? null;
    },
  },
  {
    ...defaultFormElement,
    type: 'select',
    name: 'type',
    label: t('Portal Type'),
    disabled: () => context.readOnly,
    render: () => true,
    options: () =>
      context.portals_types_enum.map(({ value }) => ({
        value,
        label: value,
      })),
  },
  {
    ...defaultFormElement,
    type: 'text',
    name: 'name',
    label: t('Name'),
    render: () => true,
    disabled: () => context.readOnly,
  },
  {
    ...defaultFormElement,
    type: 'select',
    name: 'master_portal_id',
    label: t('Shared Portal'),
    options: () =>
      context.shared_portals
        .filter(
          (item): item is { id: string; name: string } =>
            item.id != null && item.name != null,
        )
        .map(({ id, name }) => ({
          value: id,
          label: name,
        })),
    render: ({ type }) => type === Portals_Types_Enum_Enum.Shared,
    disabled: () => context.readOnly,
  },
  {
    ...defaultFormElement,
    type: 'text',
    name: 'contract_id',
    label: t('Contract ID'),
    disabled: () => context.readOnly,
  },
  {
    ...defaultFormElement,
    type: 'text',
    name: 'quota',
    label: t('Quota'),
    required: false,
    disabled: () => context.readOnly,
  },
  {
    ...defaultFormElement,
    type: 'text',
    name: 'username',
    label: t('Username'),
    disabled: () => context.readOnly,
    render: ({ type }) =>
      !!type &&
      ![
        Portals_Types_Enum_Enum.Fotocasa,
        Portals_Types_Enum_Enum.Shared,
        Portals_Types_Enum_Enum.Pisos,
      ].includes(type),
  },
  {
    ...defaultFormElement,
    type: 'text',
    name: 'password',
    label: t('Password'),
    disabled: () => context.readOnly,
    render: ({ type }) =>
      !!type &&
      ![
        Portals_Types_Enum_Enum.Fotocasa,
        Portals_Types_Enum_Enum.Shared,
        Portals_Types_Enum_Enum.Pisos,
      ].includes(type),
  },
  {
    ...defaultFormElement,
    type: 'text',
    name: 'password',
    label: t('API Key'),
    disabled: () => context.readOnly,
    render: ({ type }) => type === Portals_Types_Enum_Enum.Pisos,
  },
  {
    ...defaultFormElement,
    type: 'text',
    name: 'email',
    label: t('Email'),
    disabled: () => context.readOnly,
  },
  {
    ...defaultFormElement,
    type: 'text',
    name: 'website_url',
    label: t('Website URL'),
    required: false,
    disabled: () => context.readOnly,
  },
  {
    type: 'team',
    name: 'team_id',
    label: t('Team'),
    disabled: () => context.disableTeamId,
  },
  {
    name: 'options',
    label: t('Options'),
    type: 'checkbox-group',
    render: ({ type }) => !!type && type !== Portals_Types_Enum_Enum.Shared,
    disabled: () => context.readOnly,
    checkboxes: (
      [
        {
          name: 'active',
          label: t('Active'),
        },
        {
          name: 'has_top_premium_listing',
          label: t('Has Top Premium listings'),
        },
        {
          name: 'exclude_from_published_stats',
          label: t('Exclude from published stats'),
        },
        {
          name: 'exact_address_required',
          label: t('Exact address required'),
        },
      ] as {
        name: FieldPath<PortalSettingsFormData>;
        label: string;
      }[]
    ).map(checkbox => ({
      ...checkbox,
      type: 'checkbox',
    })),
    gridProps: {
      xs: 12,
    },
  },
  {
    type: 'category-title',
    name: 'idx_protocol_title',
    label: t('Connection settings'),
    render: ({ type }) => type === Portals_Types_Enum_Enum.Idx,
    disabled: () => context.readOnly,
  },
  {
    type: 'select',
    name: 'idx_protocol',
    label: t('Protocol'),
    render: ({ type }) => type === Portals_Types_Enum_Enum.Idx,
    options: () => [
      { value: 'ftp', label: 'FTP' },
      { value: 'sftp', label: 'SFTP' },
    ],
    gridProps: {
      xs: 12,
      md: 3,
    },
    disabled: () => context.readOnly,
  },
  {
    type: 'text',
    name: 'url',
    label: t('URL'),
    render: ({ type }) => type === Portals_Types_Enum_Enum.Idx,
    gridProps: {
      xs: 12,
      md: 7,
    },
    disabled: () => context.readOnly,
  },
  {
    type: 'text',
    name: 'port',
    label: t('Port'),
    render: ({ type }) => type === Portals_Types_Enum_Enum.Idx,
    gridProps: {
      xs: 12,
      md: 2,
    },
    disabled: () => context.readOnly,
  },
  {
    name: 'meta',
    label: t('Metadata'),
    type: 'category-title',
    render: () => !context.isCreation,
    disabled: () => context.readOnly,
  },
  {
    name: 'created_at',
    label: t('Created at'),
    type: 'date',
    render: () => !context.isCreation,
    disabled: () => true,
  },
  {
    name: 'created_by',
    label: t('Created by'),
    type: 'user',
    render: () => !context.isCreation,
    disabled: () => true,
  },
];
